import React from 'react'
import {
  Chat,
  type Question as MessageQuestion,
  useRequest,
} from './RequestsCtx'
import type { OpenedSurvey } from './types'
import type { InputProps as InputProps } from './Input'
import { UnknownError } from '@packages/common/src/errors/common-errors'
import { useOffsetContainerCtx } from './OffsetContainer'
import { maxWidgetHeight } from './use-measure-widget-dimensions'

export const useChat = (
  survey: OpenedSurvey,
  onHeightChange?: (height: number) => void
) => {
  const [loading, setLoading] = React.useState(false)
  const [finished, setFinished] = React.useState(
    () => survey.question.role === 'thanks'
  )
  const [error, setError] = React.useState<Error | null>(null)

  const [history, setHistory] = React.useState<Chat>(() => [
    {
      id: survey.question.id,
      type: 'question' as const,
      text: survey.question.text,
      role: survey.question.role,
      choices: survey.question.choices,
    },
  ])

  const offsetHeight = useOffsetContainerCtx(s => s.heightOffset)

  const postChatMessage = useRequest(s => s.surveyRespondMutationFn)

  return {
    loading,
    finished,
    error,
    history,
    addAnswer: (values: Parameters<InputProps['onSubmit']>[0]) => {
      onHeightChange?.(maxWidgetHeight + offsetHeight)

      setHistory(_history => [
        ..._history,
        { type: 'answer', text: values.text, id: '' },
      ])

      const question = history
        .filter(msg => msg.id === values.questionId)
        .at(-1)

      if (!question) return

      setLoading(true)
      postChatMessage({
        sessionId: survey.session.id,
        answer: {
          questionId: values.questionId,
          question: question.text,
          response: values.text,
          choiceId: values.choiceId,
        },
      })
        .then(response => {
          setLoading(false)

          if (response.type === 'next-question') {
            setHistory(_history => [
              ..._history,
              {
                id: response.question.id,
                role: response.question.role,
                text: response.question.text,
                type: 'question',
                choices: response.question.choices,
              },
            ])

            if (response.question.role === 'thanks') {
              setFinished(true)
            }
          } else {
            setFinished(true)
          }
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.error(e)
          setLoading(false)
          setError(e)
          setFinished(true)
        })
    },
    get lastQuestion() {
      for (let i = history.length - 1; i >= 0; i--) {
        if (history[i].type === 'question') {
          return history[i] as MessageQuestion
        }
      }

      throw new UnknownError()
    },
  }
}
